import colors from '@utils/colors';
import { sparkleBurst, SparkleOptions } from '@utils/sparkleBurst';
import {
  TASK_LIST_COMPLETE_ARRIVE_MS,
  TASK_LIST_COMPLETE_COLLAPSE_MS,
  TASK_LIST_COMPLETE_FADE_MS,
  TASK_LIST_COMPLETE_HOLD_MS,
  TASK_LIST_COMPLETE_SWEEP_MS
} from '@utils/constants/task';

const SWEEP_EASING = 'cubic-bezier(0.15, 0.85, 0.25, 1)';

/** How long the whole thing takes, from the click to the rows below having closed the gap. */
export const TASK_ROW_COMPLETION_MS =
  TASK_LIST_COMPLETE_SWEEP_MS + TASK_LIST_COMPLETE_HOLD_MS + TASK_LIST_COMPLETE_FADE_MS + TASK_LIST_COMPLETE_COLLAPSE_MS;

export type GreenSweep = {
  /** Brings every part to its end: read where things stand only after this, as a busy page can be behind on them. */
  settle: () => void;
  /** Takes it all off again: the green, the fade. */
  clear: () => void;
  /** Settles once it has faded, whenever that is - at once for a student who asks for less motion. */
  faded: Promise<void>;
};

/**
 * Green shoots across `content` from the left with a bright edge riding its front, a burst of sparkles pops at `burst`
 * as it lands, and `content` holds green a moment, then fades: a task completed in the Task List or the sidebar (Petr:
 * "a lot more fun", "more exciting and powerful"; it replaced confetti and a particle effect).
 *
 * Built straight onto the element and booked in full when called. Every part only moves or fades, which the browser
 * runs on its own, so a busy or slow computer doesn't make it stutter (Petr: "any chance some of these animations will
 * look bad on worse computers?"). Nothing moves for a student who asks for less motion.
 */
export const sweepGreen = (content: HTMLElement, burst?: { x: number; y: number } & SparkleOptions): GreenSweep => {
  const animations: Animation[] = [];
  const layers: HTMLElement[] = [];
  const { overflow, position } = content.style;
  const settle = () => animations.forEach(animation => animation.playState !== 'finished' && animation.finish());
  const clear = () => {
    animations.splice(0).forEach(animation => animation.cancel());
    layers.splice(0).forEach(layer => layer.remove());
    content.style.overflow = overflow;
    content.style.position = position;
  };
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches || typeof content.animate !== 'function') {
    return { settle, clear, faded: Promise.resolve() };
  }

  const layer = (style: Partial<CSSStyleDeclaration>, parent: HTMLElement = content) => {
    const element = document.createElement('div');
    Object.assign(element.style, {
      position: 'absolute',
      top: '0',
      bottom: '0',
      left: '0',
      right: '0',
      zIndex: '1',
      pointerEvents: 'none',
      willChange: 'transform, opacity',
      ...style
    });
    parent.appendChild(element);
    if (parent === content) layers.push(element);
    return element;
  };
  const play = (element: HTMLElement, keyframes: Keyframe[], timing: KeyframeAnimationOptions) => {
    const animation = element.animate(keyframes, timing);
    animations.push(animation);
    return animation;
  };

  // Clipped to the element, so the green slides in from its left edge. Clipped rather than hidden where the browser
  // can: a hidden row would be a scroller of its own, and the Task List's pinned columns would let go mid-sweep.
  if (getComputedStyle(content).position === 'static') content.style.position = 'relative';
  content.style.overflow = 'hidden';
  content.style.overflow = 'clip';
  const fill = layer({ background: `${colors.positive}40` });
  // The bright edge rides the front: a full-width track moving with the fill, the streak at its right end.
  const track = layer({ zIndex: '2' });
  layer(
    {
      left: 'auto',
      width: '36px',
      background: `linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.85) 70%, ${colors.positive} 100%)`
    },
    track
  );

  const wipe = [{ transform: 'translateX(-100%)' }, { transform: 'translateX(0)' }];
  const sweep = { duration: TASK_LIST_COMPLETE_SWEEP_MS, easing: SWEEP_EASING, fill: 'both' as FillMode };
  play(fill, wipe, sweep);
  play(track, wipe, sweep);
  play(track, [{ opacity: 1 }, { opacity: 1, offset: 0.8 }, { opacity: 0 }], {
    duration: TASK_LIST_COMPLETE_SWEEP_MS,
    fill: 'forwards'
  });
  const fade = play(content, [{ opacity: 1 }, { opacity: 0 }], {
    duration: TASK_LIST_COMPLETE_FADE_MS,
    delay: TASK_LIST_COMPLETE_SWEEP_MS + TASK_LIST_COMPLETE_HOLD_MS,
    easing: 'ease-in',
    fill: 'forwards'
  });
  if (burst) {
    const { x, y, ...sparkles } = burst;
    sparkleBurst(x, y, { delay: TASK_LIST_COMPLETE_ARRIVE_MS, ...sparkles });
  }

  return {
    settle,
    clear,
    // A cancelled fade (cleared early) counts as done too.
    faded: fade.finished.then(
      () => undefined,
      () => undefined
    )
  };
};

export type TaskRowCompletion = {
  /** Brings every part to its end: read the rows' places only after this, as a busy page can be behind on them. */
  settle: () => void;
  /** Takes it all off again - the green, the row's fade, the slide-up - once the list has redrawn without the row. */
  clear: () => void;
};

/**
 * A task completed in the Task List table: the green sweep on its row (`sweepGreen`), the sparkles bursting from the
 * checkbox clicked, and the rows below sliding up over it once it has faded.
 *
 * Booked in full on the click, so it starts at once however big the table is, rather than after the table redraws
 * (about 0.3 s in the dev build, more on a slow computer).
 *
 * `row` is the row's `[data-slide-row]` element, `list` the list's scrolling element, `checkbox` the box clicked.
 */
export const celebrateTaskRow = (row: HTMLElement, list: HTMLElement, checkbox?: Element): TaskRowCompletion => {
  // The task's block (its line and any subtask lines under it), so they sweep, fade and close up together.
  const content = row.querySelector<HTMLElement>('.task-block') || row.querySelector<HTMLElement>('.tr') || row;
  // Everything read before anything changes: read after a change, each row's place cost the table a fresh layout -
  // 0.13 s for a hundred rows. The rows below are found by the place the list gave them, which costs none.
  const { top, right, height } = row.getBoundingClientRect();
  // Only the task's line closes: the last task of a group also carries the group's "+ Add new task" below it, which
  // stays (it moves up with the rest) - closing all of it slid the rows over that button, then back (Petr).
  const lineHeight = content === row ? height : content.getBoundingClientRect().height;
  const rowTail = Array.from(row.children).filter(child => child !== content && !child.contains(content)) as HTMLElement[];
  // The sticky layer's sections below the row (a group's pill and labels, a course's name) move with the rows, or a
  // pill stayed where it was over its own copy sliding up in the list. Only the outermost of nested ones.
  const blocksBelow = Array.from(list.querySelectorAll<HTMLElement>('[data-slide-block]')).filter(
    block => block.getBoundingClientRect().top > top
  );
  const outerBlocks = blocksBelow.filter(block => !blocksBelow.some(other => other !== block && other.contains(block)));
  const shown = list.getBoundingClientRect();
  const box = checkbox?.getBoundingClientRect();
  const placeOf = (element: HTMLElement) => parseFloat(element.style.top);
  const below = Array.from(list.querySelectorAll<HTMLElement>('[data-slide-row]')).filter(other =>
    other === row
      ? false
      : Number.isNaN(placeOf(other)) || Number.isNaN(placeOf(row))
      ? other.getBoundingClientRect().top > top
      : placeOf(other) > placeOf(row)
  );

  // The sparkles burst from the checkbox as the green arrives at it, or from the row's end in view if the box is
  // scrolled out of sight.
  const boxInView = box && box.width > 0 && box.left >= shown.left && box.right <= shown.right;
  const sweep = sweepGreen(
    content,
    boxInView
      ? { x: box!.left + box!.width / 2, y: box!.top + box!.height / 2 }
      : { x: Math.min(right, shown.right) - 24, y: top + height / 2 }
  );

  // Once the row has faded, the rows below slide up over it and stay there until the list catches up.
  const collapse: Animation[] = [];
  if (!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    [...below, ...rowTail, ...outerBlocks].forEach(other => {
      if (typeof other.animate !== 'function') return;
      collapse.push(
        other.animate([{ transform: 'translateY(0)' }, { transform: `translateY(${-lineHeight}px)` }], {
          duration: TASK_LIST_COMPLETE_COLLAPSE_MS,
          delay: TASK_LIST_COMPLETE_SWEEP_MS + TASK_LIST_COMPLETE_HOLD_MS + TASK_LIST_COMPLETE_FADE_MS,
          easing: 'cubic-bezier(0.3, 0, 0.2, 1)',
          fill: 'forwards'
        })
      );
    });
  }

  return {
    settle: () => {
      sweep.settle();
      collapse.forEach(animation => animation.playState !== 'finished' && animation.finish());
    },
    clear: () => {
      sweep.clear();
      collapse.splice(0).forEach(animation => animation.cancel());
    }
  };
};
